import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  clearAuthTokens,
  getCmsIdentityToken,
  getRefreshToken,
  getIdToken,
  getSessionToken,
  getToken,
  setIdToken,
  setRefreshToken,
  setSessionToken,
  setToken,
} from "../api/authToken";
import {
  fetchCmsMe,
  fetchMyTenants,
  issueTenantJwt,
  AuthRequestError,
  registerOrganization as registerOrganizationApi,
  resetMyTenantsAttemptCount,
} from "../api/services/authService";
import {
  logoutWithRefreshToken,
  redirectToIamLogin,
  refreshAccessToken,
} from "../api/services/iamService";
import type {
  AuthUser,
  JwtResponse,
  RegisterOrganizationPayload,
  RegisterOrganizationResponse,
  UserTenant,
} from "../types";
import { isLocalDevAppMode } from "../utils/appMode";
import { isSamiIamEnabled } from "../utils/samiIam";
import {
  getDecodedToken,
  getEmailFromJwt,
  getTenantId,
  getTenantIdFromToken,
  isJwtExpired,
} from "../utils/jwtUtils";
import OrgSwitchOverlay from "../components/layout/OrgSwitchOverlay";
import {
  registerTenantAuthRefresh,
  unregisterTenantAuthRefresh,
} from "../api/tenantAuthRefresh";
import {
  registerSessionExpiredHandler,
  resetSessionExpiredNotification,
  tryRefreshAuth,
  unregisterSessionExpiredHandler,
} from "../api/authSession";
import { clearAllAgentSessionsForTenant } from "../lib/agentSessionStorage";
import { clearAgentApiBaseCache } from "../utils/agentApiBase";
import { clearActiveJourney } from "../lib/activeJourneyStorage";

function getAuthBase(): string {
  return (import.meta.env.VITE_AUTH_API_BASE as string | undefined) ?? "";
}

async function fetchIdpJwt(): Promise<string> {
  const base = getAuthBase();
  const url = `${base}/api/v1/auth/jwt`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`JWT fetch failed: ${res.status}`);
  const data: JwtResponse = await res.json();
  if (!data.token) throw new Error("No token in JWT response");
  return data.token;
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  tenants: UserTenant[];
  activeTenantId: string | null;
  pendingTenantSelection: boolean;
  pendingOrgSignup: boolean;
  orgSignupComplete: boolean;
  orgSignupResult: RegisterOrganizationResponse | null;
  identityEmail: string | null;
  switchingTenant: boolean;
  switchingTenantLabel: string | null;
  tenantsLoading: boolean;
  tenantsLoadError: string | null;
  loading: boolean;
  error: string | null;
  refreshTenantMemberships: () => Promise<void>;
  logout: () => Promise<void>;
  loginWithDevToken: (token: string) => Promise<void>;
  loginWithSessionToken: (devSessionToken?: string) => Promise<void>;
  startIamLogin: () => void;
  switchTenant: (tenantId: string) => Promise<void>;
  completeTenantSelection: (tenantId: string) => Promise<void>;
  registerOrganization: (
    payload: RegisterOrganizationPayload,
  ) => Promise<RegisterOrganizationResponse>;
}

function authUserFromDecodedToken(): AuthUser | null {
  const decoded = getDecodedToken();
  if (!decoded) return null;
  const tenantId = getTenantId() ?? "";
  return {
    email: decoded.email,
    name: decoded.name || decoded.email,
    roles: decoded.roles ?? [],
    permissions: decoded.permissions ?? [],
    groups: decoded.groups ?? [],
    tenantId,
  };
}

function resolveInitialTenantId(
  memberships: UserTenant[],
  sessionToken: string,
): string | null {
  if (memberships.length === 1) {
    return memberships[0].tenantId;
  }

  const fromSession = getTenantIdFromToken(sessionToken);
  if (
    fromSession &&
    memberships.some((membership) => membership.tenantId === fromSession)
  ) {
    return fromSession;
  }

  const existingTenantToken = getToken();
  const fromTenantJwt = getTenantIdFromToken(existingTenantToken);
  if (
    fromTenantJwt &&
    memberships.some((membership) => membership.tenantId === fromTenantJwt)
  ) {
    return fromTenantJwt;
  }

  return null;
}

function resolveIdentityEmail(): string | null {
  return (
    getEmailFromJwt(getCmsIdentityToken()) ?? getEmailFromJwt(getToken())
  );
}

function isUnauthorizedError(err: unknown): boolean {
  return err instanceof AuthRequestError && err.status === 401;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tenants, setTenants] = useState<UserTenant[]>([]);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(() =>
    getTenantIdFromToken(getToken()),
  );
  const [pendingTenantSelection, setPendingTenantSelection] = useState(false);
  const [pendingOrgSignup, setPendingOrgSignup] = useState(false);
  const [orgSignupComplete, setOrgSignupComplete] = useState(false);
  const [orgSignupResult, setOrgSignupResult] =
    useState<RegisterOrganizationResponse | null>(null);
  const [identityEmail, setIdentityEmail] = useState<string | null>(() =>
    resolveIdentityEmail(),
  );
  const [switchingTenant, setSwitchingTenant] = useState(false);
  const [switchingTenantLabel, setSwitchingTenantLabel] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantsLoadError, setTenantsLoadError] = useState<string | null>(null);
  const [iamRedirecting, setIamRedirecting] = useState(false);

  const handleSessionExpired = useCallback(async () => {
    clearAllAgentSessionsForTenant(activeTenantId);
    clearActiveJourney(activeTenantId);
    clearAgentApiBaseCache();
    clearAuthTokens();
    setTokenState(null);
    setUser(null);
    setTenants([]);
    setActiveTenantId(null);
    setPendingTenantSelection(false);
    setPendingOrgSignup(false);
    setOrgSignupComplete(false);
    setOrgSignupResult(null);
    setIdentityEmail(null);
    setTenantsLoading(false);
    setTenantsLoadError(null);
    setError("Your session has expired. Please sign in again.");
    setLoading(false);
    setSwitchingTenant(false);
    setSwitchingTenantLabel(null);

    if (isSamiIamEnabled()) {
      setIamRedirecting(true);
      redirectToIamLogin();
    } else {
      setIamRedirecting(false);
    }
  }, [activeTenantId]);

  const refreshTenantMemberships = useCallback(async () => {
    const bearerToken = getCmsIdentityToken() ?? getToken();
    if (!bearerToken) {
      setTenantsLoadError("Not authenticated");
      return;
    }

    setTenantsLoading(true);
    setTenantsLoadError(null);
    resetMyTenantsAttemptCount();

    try {
      const memberships = await fetchMyTenants(bearerToken);
      setTenants(memberships);
      setIdentityEmail(resolveIdentityEmail());
    } catch (err) {
      if (isUnauthorizedError(err)) {
        await handleSessionExpired();
        return;
      }
      setTenantsLoadError(
        err instanceof Error ? err.message : "Failed to load organizations",
      );
    } finally {
      setTenantsLoading(false);
    }
  }, [handleSessionExpired]);

  const activateTenantJwt = useCallback(
    async (tenantId: string) => {
      const identityToken = getCmsIdentityToken();
      if (!identityToken) {
        throw new Error("Sign in again to switch organizations");
      }

      const response = await issueTenantJwt(tenantId, identityToken);
      setToken(response.token);
      setTokenState(response.token);
      setActiveTenantId(response.tenantId);
      setPendingTenantSelection(false);

      await refreshTenantMemberships();

      try {
        const me = await fetchCmsMe(response.token);
        setUser(me);
      } catch (err) {
        if (!isUnauthorizedError(err)) {
          setUser(authUserFromDecodedToken());
        }
      }
    },
    [refreshTenantMemberships],
  );

  const bootstrapTenantSession = useCallback(
    async (devSessionToken?: string) => {
      if (devSessionToken) {
        setSessionToken(devSessionToken);
      }

      const identityToken = getCmsIdentityToken();
      if (!identityToken) {
        throw new Error("Not authenticated");
      }

      resetMyTenantsAttemptCount();
      const memberships = await fetchMyTenants(identityToken);
      setTenants(memberships);
      setTenantsLoadError(null);
      setIdentityEmail(resolveIdentityEmail());

      if (memberships.length === 0) {
        setPendingOrgSignup(true);
        setOrgSignupComplete(false);
        setOrgSignupResult(null);
        setPendingTenantSelection(false);
        return;
      }

      setPendingOrgSignup(false);
      setOrgSignupComplete(false);
      setOrgSignupResult(null);

      const tenantId = resolveInitialTenantId(memberships, identityToken);
      if (tenantId) {
        await activateTenantJwt(tenantId);
        return;
      }

      setPendingTenantSelection(true);
    },
    [activateTenantJwt],
  );

  const startIamLogin = useCallback(() => {
    setError(null);
    setIamRedirecting(true);
    redirectToIamLogin();
  }, []);

  const logout = useCallback(async () => {
    const refresh = getRefreshToken();
    const session = getSessionToken();
    const idToken = getIdToken();
    clearAllAgentSessionsForTenant(activeTenantId);
    clearActiveJourney(activeTenantId);
    clearAgentApiBaseCache();
    clearAuthTokens();
    setTokenState(null);
    setUser(null);
    setTenants([]);
    setActiveTenantId(null);
    setPendingTenantSelection(false);
    setPendingOrgSignup(false);
    setOrgSignupComplete(false);
    setOrgSignupResult(null);
    setIdentityEmail(null);
    setTenantsLoading(false);
    setTenantsLoadError(null);
    setError(null);

    if (isSamiIamEnabled() && (refresh || session || idToken)) {
      try {
        const result = await logoutWithRefreshToken(refresh, session, idToken);
        if (result.logoutUrl) {
          window.location.assign(result.logoutUrl);
          return;
        }
      } catch {
        // Local session already cleared
      }
    }
  }, [activeTenantId]);

  const loginWithSessionToken = useCallback(
    async (devSessionToken?: string) => {
      resetSessionExpiredNotification();
      setError(null);
      setLoading(true);
      try {
        await bootstrapTenantSession(devSessionToken);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Login failed");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [bootstrapTenantSession],
  );

  const loginWithDevToken = useCallback(
    (token: string) => loginWithSessionToken(token),
    [loginWithSessionToken],
  );

  const switchTenant = useCallback(
    async (tenantId: string) => {
      if (!tenantId || tenantId === activeTenantId) return;
      if (!getCmsIdentityToken()) {
        const message = "Sign in again to switch organizations";
        setError(message);
        throw new Error(message);
      }
      const targetTenant = tenants.find((tenant) => tenant.tenantId === tenantId);
      setSwitchingTenantLabel(
        targetTenant?.displayName?.trim() || tenantId,
      );
      setSwitchingTenant(true);
      setError(null);

      try {
        await activateTenantJwt(tenantId);
        window.location.reload();
      } catch (err) {
        setSwitchingTenant(false);
        setSwitchingTenantLabel(null);
        const message =
          err instanceof Error ? err.message : "Failed to switch organization";
        setError(message);
        throw err;
      }
    },
    [activeTenantId, activateTenantJwt, tenants],
  );

  const completeTenantSelection = useCallback(
    async (tenantId: string) => {
      setLoading(true);
      setError(null);
      try {
        await activateTenantJwt(tenantId);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to select organization",
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [activateTenantJwt],
  );

  const registerOrganization = useCallback(
    async (payload: RegisterOrganizationPayload) => {
      const identityToken = getCmsIdentityToken();
      if (!identityToken) {
        throw new Error("Not authenticated");
      }

      setError(null);
      const response = await registerOrganizationApi(identityToken, payload);
      setOrgSignupResult(response);
      setOrgSignupComplete(true);
      return response;
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    async function initializeAuth() {
      if (location.pathname === "/auth/callback") {
        setLoading(false);
        return;
      }

      const devMode = isLocalDevAppMode();
      const iamEnabled = isSamiIamEnabled();
      const storedTenantToken = getToken();
      const storedRefreshToken = getRefreshToken();

      try {
        if (storedTenantToken) {
          if (isJwtExpired(storedTenantToken)) {
            const refreshed = await tryRefreshAuth();
            if (!refreshed) {
              if (!cancelled) await handleSessionExpired();
              return;
            }
          }

          const identityTokenForCheck = getCmsIdentityToken();
          if (
            identityTokenForCheck &&
            isJwtExpired(identityTokenForCheck) &&
            !storedRefreshToken
          ) {
            if (!cancelled) await handleSessionExpired();
            return;
          }

          const currentTenantToken = getToken() ?? storedTenantToken;
          setToken(currentTenantToken);
          setTokenState(currentTenantToken);
          const identityToken = getCmsIdentityToken() ?? currentTenantToken;

          let memberships: UserTenant[] = [];
          resetMyTenantsAttemptCount();
          try {
            memberships = await fetchMyTenants(identityToken);
            if (!cancelled) setTenantsLoadError(null);
          } catch (err) {
            if (isUnauthorizedError(err)) {
              if (!cancelled) await handleSessionExpired();
              return;
            }
            if (!cancelled) {
              setTenantsLoadError(
                err instanceof Error
                  ? err.message
                  : "Failed to load organizations",
              );
            }
          }

          if (cancelled) return;
          setTenants(memberships);
          const tenantFromJwt = getTenantIdFromToken(currentTenantToken);
          if (tenantFromJwt) {
            setActiveTenantId(tenantFromJwt);
            try {
              const me = await fetchCmsMe(currentTenantToken);
              if (!cancelled) setUser(me);
            } catch (err) {
              if (isUnauthorizedError(err)) {
                if (!cancelled) await handleSessionExpired();
                return;
              }
              if (!cancelled) setUser(authUserFromDecodedToken());
            }
          } else if (memberships.length === 1) {
            await activateTenantJwt(memberships[0].tenantId);
          } else if (memberships.length > 1) {
            setPendingTenantSelection(true);
          } else {
            setPendingOrgSignup(true);
            setOrgSignupComplete(false);
            setOrgSignupResult(null);
            setIdentityEmail(resolveIdentityEmail());
          }
          return;
        }

        if (getCmsIdentityToken()) {
          const identityToken = getCmsIdentityToken();
          if (
            identityToken &&
            isJwtExpired(identityToken) &&
            !storedRefreshToken
          ) {
            if (!cancelled) await handleSessionExpired();
            return;
          }
          await bootstrapTenantSession();
          return;
        }

        if (iamEnabled && storedRefreshToken) {
          try {
            const refreshed = await refreshAccessToken(storedRefreshToken);
            if (refreshed.accessToken) {
              setSessionToken(refreshed.accessToken);
              if (refreshed.idToken) {
                setIdToken(refreshed.idToken);
              }
              if (refreshed.refreshToken) {
                setRefreshToken(refreshed.refreshToken);
              }
              await bootstrapTenantSession(refreshed.accessToken);
              return;
            }
          } catch {
            setRefreshToken(null);
          }
        }

        if (devMode) {
          return;
        }

        if (iamEnabled) {
          if (!cancelled) {
            setIamRedirecting(true);
            redirectToIamLogin();
          }
          return;
        }

        const isDev = import.meta.env.DEV;
        if (!isDev) {
          window.location.href = "/";
          return;
        }

        const idpToken = await fetchIdpJwt();
        if (cancelled) return;
        await bootstrapTenantSession(idpToken);
      } catch (err) {
        if (!cancelled) {
          if (isUnauthorizedError(err)) {
            await handleSessionExpired();
          } else {
            setIamRedirecting(false);
            setError(err instanceof Error ? err.message : "Auth failed");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void initializeAuth();
    return () => {
      cancelled = true;
    };
  }, [
    activateTenantJwt,
    bootstrapTenantSession,
    handleSessionExpired,
    location.pathname,
  ]);

  useEffect(() => {
    registerSessionExpiredHandler(handleSessionExpired);
    return () => unregisterSessionExpiredHandler();
  }, [handleSessionExpired]);

  useEffect(() => {
    const interval = window.setInterval(async () => {
      const tenantToken = getToken();
      if (!tenantToken || !isJwtExpired(tenantToken)) {
        return;
      }

      const refreshed = await tryRefreshAuth();
      if (refreshed) {
        const newToken = getToken();
        if (newToken) {
          setTokenState(newToken);
        }
        return;
      }

      await handleSessionExpired();
    }, 60_000);

    return () => window.clearInterval(interval);
  }, [handleSessionExpired]);

  useEffect(() => {
    registerTenantAuthRefresh(async () => {
      try {
        const tenantId =
          activeTenantId ?? getTenantIdFromToken(getToken()) ?? null;
        if (!tenantId) return false;
        await activateTenantJwt(tenantId);
        return true;
      } catch {
        return false;
      }
    });
    return () => unregisterTenantAuthRefresh();
  }, [activateTenantJwt, activeTenantId]);

  useEffect(() => {
    const tenantFromToken = getTenantIdFromToken(token);
    if (tenantFromToken) {
      setActiveTenantId(tenantFromToken);
    }
  }, [token]);

  useEffect(() => {
    if (
      loading ||
      iamRedirecting ||
      !token ||
      pendingTenantSelection ||
      pendingOrgSignup ||
      tenants.length > 0
    ) {
      return;
    }
    void refreshTenantMemberships();
  }, [
    loading,
    iamRedirecting,
    token,
    tenants.length,
    pendingTenantSelection,
    pendingOrgSignup,
    refreshTenantMemberships,
  ]);

  const isAuthCallbackRoute = location.pathname === "/auth/callback";
  const showGlobalLoadingGate = (loading || iamRedirecting) && !isAuthCallbackRoute;

  const value: AuthContextValue = {
    token,
    user,
    tenants,
    activeTenantId,
    pendingTenantSelection,
    pendingOrgSignup,
    orgSignupComplete,
    orgSignupResult,
    identityEmail,
    switchingTenant,
    switchingTenantLabel,
    tenantsLoading,
    tenantsLoadError,
    loading: loading || iamRedirecting,
    error,
    refreshTenantMemberships,
    logout,
    loginWithDevToken,
    loginWithSessionToken,
    startIamLogin,
    switchTenant,
    completeTenantSelection,
    registerOrganization,
  };

  return (
    <AuthContext.Provider value={value}>
      {showGlobalLoadingGate ? (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          {iamRedirecting ? "Redirecting to sign in…" : "Loading..."}
        </div>
      ) : (
        children
      )}
      {switchingTenant && switchingTenantLabel && (
        <OrgSwitchOverlay organizationName={switchingTenantLabel} />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
